import React from "react";
import { cn } from "@/lib/utils";
import { ArrowRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useIsMobile } from "../hooks/use-mobile";

export interface BlogPost {
  id: string;
  title: string;
  excerpt?: string;
  date: string;
  category: string;
  readTime?: string;
  author: string;
  imageUrl?: string;
  content?: string;
  MDXContent?: React.ComponentType;
  featured?: boolean;
  draft?: boolean;
}

interface BlogCardProps {
  post: BlogPost;
  className?: string;
}

const BlogCard = ({ post, className }: BlogCardProps) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const isMobile = useIsMobile();

  const handleHover = React.useCallback(() => {
    // Only set hover state on desktop
    if (!isMobile) {
      setIsHovered(true);
    }
  }, [isMobile]);

  return (
    <Link
      to={`/blog/${post.id}`}
      className={cn(
        "border border-gray-200 flex flex-col transition-all duration-200 overflow-hidden relative cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        !isMobile && isHovered ? "transform hover:-translate-y-1" : "",
        className
      )}
      onMouseEnter={handleHover}
      onMouseLeave={() => setIsHovered(false)}
    >
      {post.featured && (
        <div className="absolute top-2 right-2 z-10 flex items-center">
          {post.readTime && (
            <span className="text-xs text-muted-foreground mr-2">
              {post.readTime} min read
            </span>
          )}
          <div
            className={cn(
              "bg-primary/90 text-primary-foreground rounded-tr-md rounded-bl-md p-1 shadow-sm",
              !isMobile && isHovered ? "animate-pulse" : ""
            )}
          >
            <Star size={12} className="text-primary-foreground" />
          </div>
        </div>
      )}
      <div className="p-3 flex flex-col flex-1">
        <div className="flex flex-wrap items-center justify-between text-sm mb-0 gap-2">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{post.date}</span>
            <div
              className={cn(
                "bg-secondary/30 text-secondary-foreground px-2 py-0.5 rounded-full text-xs font-medium",
                !isMobile && isHovered ? "bg-secondary/50" : ""
              )}
            >
              {post.category}
            </div>
          </div>
          {!post.featured && post.readTime && (
            <span className="text-muted-foreground text-xs">
              {post.readTime} min read
            </span>
          )}
        </div>
        <h3
          className={cn(
            "text-base font-semibold text-foreground mb-1",
            !isMobile && isHovered ? "text-primary" : ""
          )}
        >
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-muted-foreground text-sm mb-3 flex-1 font-['Inter',sans-serif] leading-relaxed">
            {post.excerpt}
          </p>
        )}

        <span
          className={cn(
            "read-more-link flex items-center text-sm font-medium text-primary mt-auto bg-primary/5 px-2 py-1 rounded-md w-fit transition-all duration-200",
            isHovered ? "bg-primary/15 transform translate-x-1" : ""
          )}
        >
          Read more
          <ArrowRight
            size={12}
            className={cn(
              "ml-1 transition-transform duration-200",
              isHovered ? "transform translate-x-1" : ""
            )}
          />
        </span>
      </div>
    </Link>
  );
};

export default BlogCard;
